import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useMemo } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useProjectFilter } from '@/features/projects';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2TagGroup,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import {
  buildTableV2BodyRowSurfaceSx,
  tableV2RowSurfaceSx,
} from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { type TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { buildTagColorByName, resolveTagColor } from '@/features/shared/tags';
import { resolveUploadFailurePresentation } from '@/features/uploads/components/uploadFailurePresentation';
import { useUploadsTableRowActions } from '@/features/uploads/components/useUploadsTableRowActions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { useTagOptions } from '@/shared/hooks';
import { Stack } from '@/ui/layout/Stack';
import { TableCell, TableRow, Text } from '@/ui/primitives';
import { resolveDeleteUploadDialogCopy } from './UploadsTable.dialogCopy';
import { UploadStatusChip } from './UploadStatusChip';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { QueryResult, UploadDto } from '@flowqa/shared';

type UploadsTableV2Props = {
  active: boolean;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  initialLoadingFallback?: ReactElement;
  initialPageSize?: number;
  noRowsText?: string;
  pageSizeOptions?: number[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<UploadDto>>;
  queryKey: QueryKey;
  search?: string;
  sorts: GridQueryInput['sorts'];
  onQueryMeta?: (meta: { total: number; items: UploadDto[] } | null) => void;
};

const skeletonColumns = [
  'nameSingle',
  'status',
  'name',
  'dateTime',
  'nameSingle',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

const colgroup = (
  <colgroup>
    <col width="17%" />
    <col width="12%" />
    <col width="27%" />
    <col width="14%" />
    <col width="14%" />
    <col width="12%" />
    <col width="4%" />
  </colgroup>
);

const uploadsTablePanelSx = {
  '& table': {
    minWidth: '980px',
  },
} satisfies SxProps<Theme>;

const header = (
  <TableRow sx={tableV2RowSurfaceSx}>
    <TableCell>Upload</TableCell>
    <TableCell>Status</TableCell>
    <TableCell>File</TableCell>
    <TableCell>Uploaded</TableCell>
    <TableCell>Uploaded By</TableCell>
    <TableCell>Tags</TableCell>
    <TableCell align="center">
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const UploadStatusCell = ({ row }: { row: UploadDto }): ReactElement => {
  const failurePresentation = resolveUploadFailurePresentation(row);
  return (
    <Stack gap="xxs" align="flex-start">
      <UploadStatusChip status={row.status} />
      {failurePresentation ? (
        <Text role="caption" color="textSecondary">
          {failurePresentation.subkindLabel
            ? `${failurePresentation.label}: ${failurePresentation.subkindLabel}`
            : failurePresentation.label}
        </Text>
      ) : null}
    </Stack>
  );
};

const uploadBodyRowHoverSx = {
  transition: (theme) =>
    theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
  '&:hover, &:focus-within': {
    backgroundColor: (theme) => theme.app.state.hover,
  },
  '& .MuiIconButton-root:hover, & .MuiIconButton-root.Mui-focusVisible': {
    backgroundColor: (theme) => theme.app.state.hover,
  },
} satisfies SxProps<Theme>;

const buildUploadBodyRowSx = (input: { hasRenderedDivider: boolean }): SxProps<Theme> => [
  buildTableV2BodyRowSurfaceSx(input),
  uploadBodyRowHoverSx,
];

const UploadRow = ({
  deleteRowPendingId,
  isLastVisibleRow,
  row,
  tagColorByName,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  row: UploadDto;
  tagColorByName: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: UploadDto) => void;
  onUpdateRowTagsColor: (input: { row: UploadDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const uploadedBy = row.createdByName ?? row.createdByEmail ?? '-';
  const fileHref = row.resultsFileSlug ? `/results-files/${row.resultsFileSlug}` : null;
  const uploadHref = `/uploads/${row.id}`;
  const tags = row.tags ?? [];
  const tagColors = tags.map((tag) => resolveTagColor(tag, tagColorByName));

  return (
    <TableRow sx={buildUploadBodyRowSx({ hasRenderedDivider: !isLastVisibleRow })}>
      <TableCell>
        <TableV2NameCell href={uploadHref} primary={row.id} />
      </TableCell>
      <TableCell>
        <UploadStatusCell row={row} />
      </TableCell>
      <TableCell>
        <TableV2NameCell
          href={fileHref ?? uploadHref}
          primary={row.fileName}
          secondary={row.contentType}
        />
      </TableCell>
      <TableCell>
        <TableV2DateTimeCell value={row.createdAt} />
      </TableCell>
      <TableCell>
        <Text role="body" color="textSecondary">
          {uploadedBy}
        </Text>
      </TableCell>
      <TableCell>
        <TableV2TagGroup tagColorByName={tagColorByName} tags={tags} />
      </TableCell>
      <TableCell align="center">
        <TableActionsCell
          rowName={row.fileName}
          verticalAlign="center"
          deleteActionLabel={getEntityActionLabel('delete', 'Upload')}
          editTagsEntityNoun="upload"
          rowTagColors={tagColors}
          rowTags={tags}
          onUpdateTagsColor={(input) => {
            onUpdateRowTagsColor({ row, ...input });
          }}
          updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
          onDelete={() => {
            onDeleteRow(row);
          }}
          disableDelete={deleteRowPendingId === row.id}
        />
      </TableCell>
    </TableRow>
  );
};

export const UploadsTableV2 = ({
  active,
  fixedFilters,
  forceLoading = false,
  initialLoadingFallback,
  initialPageSize = 10,
  noRowsText = 'No data',
  onQueryMeta,
  pageSizeOptions,
  queryFn,
  queryKey,
  search,
  sorts,
}: UploadsTableV2Props): ReactElement => {
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useUploadsTableRowActions({ selectedProjectId });
  const deleteDialogCopy = resolveDeleteUploadDialogCopy(deleteTarget?.purpose);

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Uploads"
        colgroup={colgroup}
        emptyText={noRowsText}
        entityLabel="uploads"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        initialLoadingFallback={initialLoadingFallback}
        initialPageSize={initialPageSize}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={sorts}
        sx={uploadsTablePanelSx}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <UploadRow
            key={row.id}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            row={row}
            tagColorByName={tagColorByName}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
          />
        )}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={deleteDialogCopy.title}
        description={deleteDialogCopy.description}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'upload' })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
